import React, { useState, useEffect, useMemo, useRef, ReactNode } from 'react'

import { theme } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/components'
import { Text as MantineText } from '@hhgtech/hhg-components/mantine'
import { Close } from '@hhgtech/icons/core'
import { Premium as PremiumIcon } from '@hhgtech/icons/other'
import { Box, BoxProps, Container } from '@mantine/core'
import cls from 'classnames'
import dynamic from 'next/dynamic'
import { useIntl } from 'react-intl'

import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { ContentSponsor } from 'interfaces/types'

import { StyledStickySponsoredPopup } from './index.styled'
import useStyles from './index.styles'
import {
  assetImagePath,
  assetSvgPath,
  STATIC_PAGES,
} from '../../../interfaces/constants'

const PremiumBadge = dynamic(() =>
  import('components/atoms/premiumBadge').then((mod) => mod.PremiumBadge),
)

export type Props = {
  image: string
  isMobile: boolean
  href?: string
  className?: string
} & BoxProps

const SponsoredBy = ({
  image,
  href,
  className,
  isMobile,
  classNames,
  styles,
  ...rest
}: Props) => {
  const { formatMessage: f } = useIntl()
  const { classes, cx } = useStyles(undefined, {
    name: 'SponsoredBy',
    classNames,
    styles,
  })

  return (
    <Box
      className={cx('dfdfdfdf', className, classes.root)}
      data-type-mobile={isMobile}
      {...rest}
    >
      <Text size="c2" type="bold" color="#737373">
        {f({
          id: 'articlePage.sponsoredBy',
        }).toUpperCase() + ':'}
      </Text>
      <a
        style={{
          flex: 1,
        }}
        href={href}
        target="_blank"
      >
        <img
          src={image}
          style={{
            height: '22px',
            objectPosition: isMobile ? 'right' : 'left',
            objectFit: 'contain',
            paddingLeft: '1rem',
          }}
          alt=""
          loading="lazy"
        />
      </a>
    </Box>
  )
}

export type StickyProps = {
  className?: string
  isMobile?: boolean
  isCollapsed?: boolean
  sponsoredType?: 'article' | 'content'
  sponsoredBy?: ContentSponsor[]
} & BoxProps
const StickySponsoredBy = ({
  className,
  isMobile,
  isCollapsed,
  sponsoredType,
  sponsoredBy,
  classNames,
  styles,
}: StickyProps) => {
  const { formatMessage: f } = useIntl()
  const { classes, cx } = useStyles(undefined, {
    name: 'StickySponsoredBy',
    classNames,
    styles,
  })
  const [showPopup, setShowPopup] = useState(false)
  const sponsorWrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isCollapsed) setShowPopup(false)
  }, [isCollapsed])

  const isContainLogo = useMemo(
    () =>
      sponsoredBy !== undefined &&
      sponsoredBy.some((sponsor) => !!sponsor?.logo),
    [sponsoredBy],
  )

  return (
    <Box
      className={cx(className, classes.root)}
      onClick={() => setShowPopup(true)}
      data-show-popup={showPopup && !isCollapsed}
      data-align-left={isContainLogo}
      data-sticky-sponsor="true"
    >
      {/* Do not remove the class `hhg-disclaimer-text`. 
          It is used to detect article sponsored or not on LeadGen popup */}
      <Container
        className={cx('hhg-disclaimer-text', classes.container)}
        data-sponsored-id={sponsoredBy?.[0]?.id}
        size={'lg'}
        w={'100%'}
      >
        {sponsoredType === 'content' ? (
          <div className={classes.textWrapper}>
            <div className="sticky-sponsored-info-icon">
              <img
                src={`${assetSvgPath}/info-light.svg`}
                style={{
                  width: 20,
                  height: 20,
                  marginRight: '0.3rem',
                }}
                alt=""
                loading="lazy"
              />
            </div>
            {isContainLogo ? (
              <>
                <Text
                  size="p4"
                  style={{ display: 'inline-block' }}
                  color="#737373"
                >
                  {f({
                    id: 'articlePage.sponsoredContentBy',
                    defaultMessage: 'Content sponsored by',
                  })}
                </Text>
                <div
                  className={classes.logoWrapper}
                  data-sponsor={sponsoredBy && sponsoredBy?.length > 0}
                  ref={sponsorWrapperRef}
                >
                  {sponsoredBy?.map((sponsorDetail, i) => (
                    <React.Fragment key={i}>
                      {sponsorDetail?.logo && (
                        <img
                          className={cx('sponsored-logo', classes.logo)}
                          src={sponsorDetail?.logo || ''}
                          alt={sponsorDetail?.title || 'sponsored-logo'}
                          loading="lazy"
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </>
            ) : (
              <Text size="p4" color="#737373" as="span">
                {f({
                  id: 'articlePage.sponsoredContent',
                  defaultMessage: 'Sponsored Content',
                })}
              </Text>
            )}
            {!isMobile && sponsoredBy && sponsoredBy?.length > 1 && (
              <div
                className={classes.scrollBtn}
                style={{
                  display:
                    sponsorWrapperRef?.current &&
                    sponsorWrapperRef.current.scrollWidth >
                      sponsorWrapperRef.current.offsetWidth
                      ? 'flex'
                      : 'none',
                }}
              >
                <img
                  alt=""
                  loading="lazy"
                  className={classes.img}
                  src={`${assetImagePath}/chevronLeftCircle.svg`}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (sponsorWrapperRef.current) {
                      sponsorWrapperRef.current.scrollLeft -= 50
                    }
                  }}
                />
                <img
                  loading="lazy"
                  alt=""
                  className={classes.img}
                  src={`${assetImagePath}/chevronRightCircleBlack.svg`}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (sponsorWrapperRef.current) {
                      sponsorWrapperRef.current.scrollLeft += 50
                    }
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <Text size="p2" color="#737373">
            <div>
              {f({
                id: 'articlePage.sponsoredArticle',
              })}
              <div className="sticky-sponsored-info-icon">
                <img
                  src={`${assetSvgPath}/info-light.svg`}
                  style={{
                    width: 20,
                    height: 20,
                    verticalAlign: 'middle',
                    marginLeft: '0.3rem',
                    marginBottom: '0.1rem',
                  }}
                  alt=""
                  loading="lazy"
                />
              </div>
            </div>
          </Text>
        )}
        {showPopup && !isCollapsed ? (
          <SponsoredPopup
            sponsoredBy={sponsoredBy}
            onClose={() => setShowPopup(false)}
          />
        ) : null}
      </Container>
    </Box>
  )
}

const StickySponsoredByRevamp = ({
  isPremium,
  isSponsored,
  sponsoredBy,
  className,
}: StickyProps & { isPremium?: boolean; isSponsored?: boolean }) => {
  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()
  const [showPopup, setShowPopup] = useState(false)

  const { logo, displayName, title } = useMemo(() => {
    return sponsoredBy?.[0] || {}
  }, [sponsoredBy])

  if (!isSponsored && isPremium) {
    return <PremiumBadge className={className} />
  }

  return (
    <Box
      onClick={() => setShowPopup(true)}
      className={cls(
        'hhg-disclaimer-text flex gap-3 relative cursor-pointer',
        className,
      )}
      data-sponsored-id={sponsoredBy?.[0]?.id}
    >
      {isPremium && <PremiumIcon size={20} />}
      <Box className="flex items-center gap-0.5">
        <MantineText size="p4" color={theme.colors.gray600}>
          {displayName || logo
            ? f({
                id: 'articlePage.sponsoredContentBy',
                defaultMessage: 'Content sponsored by',
              })
            : f({
                id: 'articlePage.sponsoredContent',
                defaultMessage: 'Sponsored Content',
              })}
          &nbsp;
          {displayName ? (
            <span className="underline">{displayName}</span>
          ) : logo ? (
            <img
              loading="lazy"
              src={logo}
              alt={title}
              className="min-h-9 h-9 align-middle"
            />
          ) : null}
        </MantineText>
        <Box className="relative" onClick={() => setShowPopup(true)}>
          <img
            src={`${assetSvgPath}/info-${showPopup ? 'light' : 'dark'}.svg`}
            alt=""
            loading="lazy"
            className="w-4 h-4 cursor-pointer block"
          />
          {showPopup && !isMobile && (
            <SponsoredPopup
              sponsoredBy={sponsoredBy}
              onClose={() => setShowPopup(false)}
              className="!left-0"
            />
          )}
        </Box>
      </Box>
      {showPopup && isMobile && (
        <SponsoredPopup
          sponsoredBy={sponsoredBy}
          onClose={() => setShowPopup(false)}
          className="!left-0 !translate-x-0 !top-10"
        />
      )}
    </Box>
  )
}

type PopupProps = {
  onClose?: () => void
  className?: string
  sponsoredBy?: ContentSponsor | ContentSponsor[]
}

const SponsoredPopup = ({ sponsoredBy, onClose, className }: PopupProps) => {
  const { locale } = useRouterExtend()
  const { formatMessage: f } = useIntl()

  const policyUrl = `/static${
    STATIC_PAGES[locale]?.ADVERTISING_SPONSOR_POLICY || '/'
  }`

  return (
    <StyledStickySponsoredPopup
      data-align-left={
        sponsoredBy && (Array.isArray(sponsoredBy) || !!sponsoredBy?.logo)
      }
      className={className}
      id="sponsored-by"
    >
      <Box
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onClick={(evt: any) => {
          evt.stopPropagation()
          onClose?.()
        }}
        className="absolute top-2 right-2 cursor-pointer"
      >
        <Close size={18} />
      </Box>
      <MantineText size="p4">
        {f(
          {
            id: 'articlePage.sponsoredByStickyPopup.content',
          },
          {
            a: (children: ReactNode) => {
              return (
                <a
                  href={policyUrl}
                  target="_blank"
                  className="underline text-primaryBase"
                >
                  {children}
                </a>
              )
            },
          },
        )}
      </MantineText>
    </StyledStickySponsoredPopup>
  )
}

export {
  SponsoredBy,
  StickySponsoredBy,
  SponsoredPopup,
  StickySponsoredByRevamp,
}
